import type { ThreeEvent } from '@react-three/fiber';
import { memo, useMemo } from 'react';
import { Object3D, type Raycaster, Vector3 } from 'three';
import type { HexTileMap, HexTileMapKey } from '../model/types';
import { GROUND_Y, groundPoint, groundTileKey } from './groundPick';

/**
 * An invisible ground plane at the top of land tiles, hit only over the map's
 * own tiles. It takes the board's pointer events in place of raycasting every
 * tile mesh: one ray–plane test per event. The hit's `point` is on the plane,
 * so `worldToAxial(point)` is the tile (the same tile `cursorTileKey` gives).
 */
function createPicker(tiles: HexTileMap): Object3D {
  const picker = new Object3D();
  picker.raycast = (raycaster: Raycaster, intersects) => {
    const hit = groundPoint(raycaster.ray);
    if (!hit || hit.t < raycaster.near || hit.t > raycaster.far) return;
    if (!tiles.has(groundTileKey(hit.x, hit.z))) return;
    intersects.push({
      distance: hit.t,
      point: new Vector3(hit.x, GROUND_Y, hit.z),
      object: picker,
    });
  };
  return picker;
}

const TilePicker = memo(function TilePicker({
  tiles,
  onPointerDown,
}: {
  tiles: HexTileMap;
  onPointerDown?: (key: HexTileMapKey, event: ThreeEvent<PointerEvent>) => void;
}) {
  const picker = useMemo(() => createPicker(tiles), [tiles]);
  return (
    <primitive
      object={picker}
      onPointerDown={
        onPointerDown &&
        ((event: ThreeEvent<PointerEvent>) =>
          onPointerDown(groundTileKey(event.point.x, event.point.z), event))
      }
    />
  );
});

export default TilePicker;
